import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyType: '',
    numberOfUnits: '',
    tenantSituation: '',
    message: '',
    transactionalConsent: false,
    marketingConsent: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'transactionalConsent' || key === 'marketingConsent') {
          formBody.append(key, value ? 'Yes' : 'No');
        } else {
          formBody.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d5rkocetc768qf4bqkn0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          propertyType: '',
          numberOfUnits: '',
          tenantSituation: '',
          message: '',
          transactionalConsent: false,
          marketingConsent: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Get Your Cash Offer Today
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to sell your tenant occupied property? Contact us now for a free, no-obligation cash offer. We respond within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h3 className="text-2xl font-bold mb-6">Request Your Cash Offer</h3>
            <form id="rental-property-contact" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold mb-2">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold mb-2">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold mb-2">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                    placeholder="(915) 555-0123"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-semibold mb-2">Property Address *</label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                  placeholder="123 Main St, El Paso, TX 79901"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="propertyType" className="block text-sm font-semibold mb-2">Property Type *</label>
                  <select
                    id="propertyType"
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-teal-400 transition-colors text-sm cursor-pointer"
                  >
                    <option value="" className="bg-gray-800">Select Type</option>
                    <option value="Single-Family" className="bg-gray-800">Single-Family Rental</option>
                    <option value="Duplex" className="bg-gray-800">Duplex</option>
                    <option value="Triplex" className="bg-gray-800">Triplex</option>
                    <option value="Fourplex" className="bg-gray-800">Fourplex</option>
                    <option value="Small Apartment" className="bg-gray-800">Small Apartment (5-20 units)</option>
                    <option value="Large Apartment" className="bg-gray-800">Large Apartment (20+ units)</option>
                    <option value="Mixed-Use" className="bg-gray-800">Mixed-Use</option>
                    <option value="Other" className="bg-gray-800">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="numberOfUnits" className="block text-sm font-semibold mb-2">Number of Units *</label>
                  <input
                    type="number"
                    id="numberOfUnits"
                    name="numberOfUnits"
                    value={formData.numberOfUnits}
                    onChange={handleChange}
                    required
                    min="1"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                    placeholder="1"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="tenantSituation" className="block text-sm font-semibold mb-2">Tenant Situation *</label>
                <select
                  id="tenantSituation"
                  name="tenantSituation"
                  value={formData.tenantSituation}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-teal-400 transition-colors text-sm cursor-pointer"
                >
                  <option value="" className="bg-gray-800">Select Situation</option>
                  <option value="Good Tenants" className="bg-gray-800">Good Tenants, Paying on Time</option>
                  <option value="Problem Tenants" className="bg-gray-800">Problem Tenants</option>
                  <option value="Non-Paying" className="bg-gray-800">Non-Paying Tenants</option>
                  <option value="Mixed" className="bg-gray-800">Mix of Good and Problem Tenants</option>
                  <option value="Section 8" className="bg-gray-800">Section 8 Tenants</option>
                  <option value="Month-to-Month" className="bg-gray-800">Month-to-Month Tenants</option>
                  <option value="Other" className="bg-gray-800">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold mb-2">Additional Details</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors resize-none text-sm"
                  placeholder="Tell us more about your property and situation... (500 characters max)"
                ></textarea>
                <div className="text-right text-sm text-gray-400 mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="rental-transactionalConsent"
                    name="transactionalConsent"
                    required
                    checked={formData.transactionalConsent}
                    onChange={handleChange}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="rental-transactionalConsent" className="ml-3 text-xs text-gray-300 leading-relaxed cursor-pointer">
                    <span className="text-red-500">*</span> I consent to receive transactional messages from TREX RE LLC to the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                  </label>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="rental-marketingConsent"
                    name="marketingConsent"
                    checked={formData.marketingConsent}
                    onChange={handleChange}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="rental-marketingConsent" className="ml-3 text-xs text-gray-300 leading-relaxed cursor-pointer">
                    I consent to receive marketing and promotional messages from TREX RE LLC at the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Get My Cash Offer'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-500/20 border border-green-500 text-green-100 px-4 py-3 rounded-lg">
                  Thank you! We'll contact you within 24 hours with your cash offer.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg">
                  Something went wrong. Please call us at (915) 206-5814.
                </div>
              )}
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Call Us</div>
                    <a href="tel:+19152065814" className="text-teal-400 hover:text-teal-300 text-lg cursor-pointer">
                      (915) 206-5814
                    </a>
                    <p className="text-gray-400 text-sm mt-1">Monday - Sunday: 8:00 AM - 8:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Email Us</div>
                    <a href="mailto:ElPaso@trexrealestate.com" className="text-teal-400 hover:text-teal-300 cursor-pointer">
                      ElPaso@trexrealestate.com
                    </a>
                    <p className="text-gray-400 text-sm mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Visit Us</div>
                    <p className="text-gray-300">
                      3016 Gull Lake Pl<br />
                      El Paso, TX 79936
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4">Why Choose T-Rex Real Estate?</h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <i className="ri-check-double-fill text-white text-xl"></i>
                  <span>200+ Rental Properties Purchased</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-double-fill text-white text-xl"></i>
                  <span>$120M+ Total Transaction Value</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-double-fill text-white text-xl"></i>
                  <span>100% Title Company Closings</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-double-fill text-white text-xl"></i>
                  <span>7-14 Day Average Closing Time</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-double-fill text-white text-xl"></i>
                  <span>All-Cash Offers, No Fees</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-double-fill text-white text-xl"></i>
                  <span>Buy With Tenants in Place</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-xl font-bold mb-4">Prefer to Talk?</h3>
              <p className="text-gray-300 mb-6">
                Call us now for an immediate response. We're available 7 days a week to discuss your tenant occupied property.
              </p>
              <a 
                href="tel:+19152065814"
                className="inline-flex items-center bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-bold transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill mr-2"></i>
                Call (915) 206-5814 Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}